import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Exchange from './pages/Exchange';
import PaymentConfirmation from './pages/PaymentConfirmation';
import Business from './pages/Business';
import Admin from './pages/Admin';
import Privacy from './pages/Privacy';
import PaymentCards from './pages/PaymentCards';
import CookiesPolicy from './pages/CookiesPolicy';
import Agreement from './pages/Agreement';
import KYC from './pages/KYC';
import TradingDirect from './pages/TradingDirect';
import SendMoney from './pages/SendMoney';
import PriceAlerts from './pages/PriceAlerts';
import SupportChat from './components/SupportChat';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './i18n/LanguageContext';
import './App.css';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // If user data passed from AuthCallback, skip loading
  if (location.state?.user) {
    return children;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0055FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-gray-900 text-lg font-medium">Chargement...</div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Router component that handles session_id detection for Google OAuth
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/cards" element={<PaymentCards />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/kyc" element={
        <ProtectedRoute>
          <KYC />
        </ProtectedRoute>
      } />
      <Route path="/exchange" element={<Exchange />} />
      <Route path="/payment-confirmation" element={<PaymentConfirmation />} />
      <Route path="/business" element={<Business />} />
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <Admin />
        </ProtectedRoute>
      } />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/cookies" element={<CookiesPolicy />} />
      <Route path="/agreement" element={<Agreement />} />
      <Route path="/send-money" element={<SendMoney />} />
      <Route path="/trading" element={<TradingDirect />} />
      <Route path="/alerts" element={
        <ProtectedRoute>
          <PriceAlerts />
        </ProtectedRoute>
      } />
      </Routes>
      {!location.pathname.startsWith('/admin') && <SupportChat />}
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: '#ffffff',
                color: '#111827',
                border: '1px solid #E5E7EB',
                borderRadius: '16px',
                boxShadow: '0 8px 30px rgb(0 0 0 / 0.08)',
              },
              success: { iconTheme: { primary: '#10B981', secondary: '#ffffff' } },
              error: { iconTheme: { primary: '#EF4444', secondary: '#ffffff' } },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
